import { ArrowLeft, RefreshCw, ScrollText, Search } from "lucide-react";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAdminLogs,
  type LogEntry,
  type LogLivello,
  type LogServizio,
} from "@/hooks/useAdminLogs";
import { useMe } from "@/hooks/useMe";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";

const LIVELLI: LogLivello[] = ["DEFAULT", "DEBUG", "INFO", "WARNING", "ERROR"];

const LIVELLO_COLORI: Record<string, string> = {
  DEBUG: "bg-muted text-muted-foreground border-muted-foreground/20",
  INFO: "bg-primary/10 text-primary border-primary/30",
  NOTICE: "bg-primary/10 text-primary border-primary/30",
  WARNING: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800",
  ERROR: "bg-destructive/10 text-destructive border-destructive/30",
  CRITICAL: "bg-destructive/20 text-destructive border-destructive/40",
};

function formatDataOra(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    day: "2-digit",
    month: "short",
  });
}

export function AdminLogsPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const [service, setService] = useState<LogServizio>("core");
  const [level, setLevel] = useState<LogLivello>("INFO");
  const [minuti, setMinuti] = useState(60);
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");

  const { data, isLoading, refetch, isFetching } = useAdminLogs({
    service,
    level,
    minuti,
    q: appliedQ || undefined,
    limit: 100,
  });

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  function applyQ() {
    setAppliedQ(q.trim());
  }

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Backoffice Maitime
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Log applicativi
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Log dei servizi Cloud Run filtrabili per livello e periodo. Auto-refresh ogni 15s.
        </p>
      </header>

      <div className="rounded-lg border bg-card p-3 flex flex-wrap gap-3 items-end">
        <Field label="Servizio">
          <select
            value={service}
            onChange={(e) => setService(e.target.value as LogServizio)}
            className="rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="core">core (backend API)</option>
            <option value="web">web (frontend nginx)</option>
          </select>
        </Field>
        <Field label="Livello min.">
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value as LogLivello)}
            className="rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {LIVELLI.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Finestra">
          <select
            value={minuti}
            onChange={(e) => setMinuti(parseInt(e.target.value, 10))}
            className="rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value={15}>Ultimi 15 min</option>
            <option value={60}>Ultima ora</option>
            <option value={360}>Ultime 6 ore</option>
            <option value={1440}>Ultimo giorno</option>
            <option value={4320}>Ultimi 3 giorni</option>
          </select>
        </Field>
        <Field label="Cerca">
          <div className="flex gap-1">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyQ()}
              placeholder="testo nel payload"
              className="rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring w-48"
            />
            <Button variant="ghost" size="sm" onClick={applyQ}>
              <Search className="size-3.5" />
            </Button>
          </div>
        </Field>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          className="ml-auto"
        >
          <RefreshCw className={cn("size-3.5", isFetching && "animate-spin")} />
          Aggiorna
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : data?.info ? (
        <div className="rounded-lg border bg-card/40 p-6 text-center text-sm text-muted-foreground">
          <ScrollText className="size-6 mx-auto mb-2" />
          {data.info}
        </div>
      ) : !data || data.entries.length === 0 ? (
        <div className="rounded-lg border bg-card/40 p-6 text-center text-sm text-muted-foreground">
          Nessun log nel periodo selezionato.
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-3 py-2 w-[110px]">Quando</th>
                <th className="text-left font-medium px-3 py-2 w-[90px]">Livello</th>
                <th className="text-left font-medium px-3 py-2">Payload</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.entries.map((entry, idx) => (
                <LogRow key={`${entry.timestamp}-${idx}`} entry={entry} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageContainer>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}

function LogRow({ entry }: { entry: LogEntry }) {
  const colore = LIVELLO_COLORI[entry.severity ?? ""] ?? LIVELLO_COLORI.INFO;
  const sintesi = renderPayloadSintesi(entry.payload);
  return (
    <tr className="hover:bg-muted/20">
      <td className="px-3 py-2 text-[11px] text-muted-foreground whitespace-nowrap tabular-nums align-top">
        {formatDataOra(entry.timestamp)}
      </td>
      <td className="px-3 py-2 align-top">
        <Badge variant="outline" className={cn("text-[10px]", colore)}>
          {entry.severity ?? "DEFAULT"}
        </Badge>
      </td>
      <td className="px-3 py-2 align-top">
        <div className="font-mono text-[11px] whitespace-pre-wrap break-words">
          {sintesi}
        </div>
      </td>
    </tr>
  );
}

function renderPayloadSintesi(payload: LogEntry["payload"]): string {
  if (payload == null) return "(no payload)";
  if (typeof payload === "string") return payload;
  // structlog rende eventi in JSON tipo { event: "...", ...kwargs }.
  if (typeof payload === "object") {
    const obj = payload as Record<string, unknown>;
    const event = obj.event ?? obj.message ?? "";
    const rest = Object.entries(obj)
      .filter(([k]) => k !== "event" && k !== "message")
      .map(([k, v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`)
      .join(" ");
    return [event, rest].filter(Boolean).join("  ");
  }
  return String(payload);
}
